import React from "react";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

import {
  CheckCell,
  DataTableColumn,
  DateCell,
  EMPTY_VALUE,
  EntityCell,
  NumberCell,
  StatusBadge,
  TextCell,
  Tone,
  formatTableDate,
} from "Components/UI/DataTable";
import { VEHICLE_STATUS } from "Containers/Vehicle/VehicleList/vehicleColumns";
import { IFault } from "Shared/Types/faults";

/** Fault and fault rule priority (`off` has no badge). */
export const FAULT_PRIORITY: Record<string, { label: string; tone: Tone }> = {
  critical: { label: "Critical", tone: "danger" },
  ignore: { label: "Ignore", tone: "neutral" },
};

export const FAULT_STATUS: Record<string, { label: string; tone: Tone }> = {
  open: { label: "Open", tone: "warning" },
  pending: { label: "Pending", tone: "warning" },
  resolved: { label: "Resolved", tone: "success" },
  ignored: { label: "Ignored", tone: "neutral" },
  ignore: { label: "Ignored", tone: "neutral" },
};

const badge = (
  map: Record<string, { label: string; tone: Tone }>,
  value?: string | null,
) => {
  if (!value) return EMPTY_VALUE;
  const item = map[value];
  return (
    <StatusBadge tone={item?.tone ?? "neutral"}>
      {item?.label ?? value}
    </StatusBadge>
  );
};

/** The list returns the linked issue as an id; older payloads nest the issue. */
export const issueLabel = (issue: IFault["issue"] | string | null | undefined) => {
  if (!issue) return EMPTY_VALUE;
  if (typeof issue === "string") return "Linked";
  if (issue.reference_code) return `#${issue.reference_code}`;
  return issue.summary || "Linked";
};

export const getFaultColumns = (): DataTableColumn<IFault>[] => [
  {
    id: "vehicle",
    header: "Vehicle",
    meta: { width: 180 },
    cell: ({ row }) => {
      const vehicle = row.original.device?.vehicle;
      return (
        <EntityCell
          icon={<LocalShippingOutlinedIcon />}
          image={vehicle?.image?.file}
          title={vehicle?.name || EMPTY_VALUE}
        />
      );
    },
  },
  {
    id: "code",
    header: "Code",
    meta: { width: 100 },
    cell: ({ row }) => row.original.code || EMPTY_VALUE,
  },
  {
    id: "priority",
    header: "Critical",
    meta: { width: 90, align: "center" },
    cell: ({ row }) => (
      <CheckCell value={row.original.priority === "critical"} label="Critical" />
    ),
  },
  {
    id: "name",
    header: "Name",
    meta: { truncate: true },
    cell: ({ row }) => (
      <TextCell strong primary={row.original.name || EMPTY_VALUE} />
    ),
  },
  {
    id: "updated",
    header: "Last occurred",
    meta: { width: 150 },
    cell: ({ row }) => (
      <DateCell
        value={row.original.updated}
        secondary={formatTableDate(row.original.updated, "time") ?? undefined}
      />
    ),
  },
  {
    id: "status",
    header: "Status",
    meta: { width: 130 },
    cell: ({ row }) => badge(FAULT_STATUS, row.original.status),
  },
  {
    id: "issue",
    header: "Issue",
    meta: { width: 110 },
    cell: ({ row }) => issueLabel(row.original.issue),
  },
  {
    id: "source_address_name",
    header: "Source",
    meta: { width: 150 },
    cell: ({ row }) => (
      <TextCell primary={row.original.source_address_name || EMPTY_VALUE} />
    ),
  },
  {
    id: "vehicle_group",
    header: "Vehicle group",
    meta: { width: 150 },
    cell: ({ row }) => (
      <TextCell
        primary={row.original.device?.vehicle?.group?.name || EMPTY_VALUE}
      />
    ),
  },
  {
    id: "vehicle_status",
    header: "Vehicle status",
    meta: { width: 150 },
    cell: ({ row }) =>
      badge(VEHICLE_STATUS, row.original.device?.vehicle?.status),
  },
  {
    id: "vehicle_type",
    header: "Vehicle type",
    meta: { width: 140 },
    cell: ({ row }) => (
      <TextCell
        primary={row.original.device?.vehicle?.type?.name || EMPTY_VALUE}
      />
    ),
  },
  {
    id: "occurrence_count",
    header: "Count",
    meta: { width: 90, align: "right" },
    cell: ({ row }) => <NumberCell value={row.original.occurrence_count} />,
  },
];
